import Configuration from '@/agilis/util/Configuration'
import FileUploaderSingleElement from './FileUploaderSingleElement.vue'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

const FILE_UPLOAD_DRAG_OVER_CLASSES = ' file-upload--dragging'

export default {
  props: {
    propTitle: String,
    trackerDTO: Object
  },
  components: {
    FileUploaderSingleElement
  },
  data () {
    return {
      fileArray: [],
      keyReset: 0,
      keyResetTemplate: 1000
    }
  },
  mounted () {
    this.fetchFiles()
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      progress: 'loaderStore/progress',
      validationConfiguration: 'configurationStore/uploadValidation'
    }),

    userCanAddAttachment () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'attachment', 'post', this.trackerDTO, this.userId)
    },

    userCanAddTemplate () {
      const privId = `/${this.selectedContext.applicationId}`
        .concat(`/workspace/${this.selectedContext.workspaceId}`)
        .concat(`/tracker/${this.selectedContext.trackerTypeId}/attachmentTemplate?op=post`)

      return this.userCanAddAttachment &&
        Configuration.auth.userHasPrivilege(privId)
    },

    userCanRemoveAttachment () {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'attachment', 'delete', this.trackerDTO, this.userId)
    },

    title () {
      return this.propTitle
    },

    accept () {
      return this.validationRules.accepted
    },

    acceptTemplate () {
      return this.validationRules.acceptedTemplate
    },

    validationRules () {
      return {
        accepted: this.validationConfiguration.accept || [],
        acceptedTemplate: this.validationConfiguration.acceptTemplate || [],
        attachmentMaxSize: this.validationConfiguration.attachmentMaxSize || 50,
        attachmentMaxNameLength: this.validationConfiguration.attachmentattachmentMaxNameLength || 255
      }
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader',
      'startProgress': 'loaderStore/startProgress',
      'updateProgress': 'loaderStore/updateProgress',
      'stopProgress': 'loaderStore/stopProgress'
    }),

    fetchFiles () {
      // attachments require openkm in the environment
      if (this.$envmap.VUE_APP_AGILIS_ATTACHMENT_DISABLE !== true) {
        this.startLoader({ id: 'tracker-attachments' })
        const url = Vue.axios.agilisUrlTrackerAttachments(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId)
        Vue.axios.get(url, Vue.axios.agilisRequestConfig(url)).then(response => {
          this.fileArray = response.data.trackerAttachments || []
        }).catch(error => {
          this.notifyError(error)
        }).finally(() => {
          this.stopLoader({ id: 'tracker-attachments' })
        })
      }
    },

    chooseFiles () {
      this.$refs.fileUpload.click()
    },

    onFilePicked (event) {
      const files = event.target.files
      let formData = new FormData()
      let tempFileArray = []
      let droppedFiles = []

      const uploadID = Date.now()
      this.startProgress({ id: 'attachments-progress', value: uploadID })

      for (var i = 0; i < files.length; i++) {
        const validation = this.validateFile(files[i])
        if (validation.valid === true) {
          formData.append('files[' + i + ']', files[i])
          tempFileArray.push({ id: uploadID, contentSize: files[i].size, filename: files[i].name })
        } else {
          droppedFiles.push({
            file: files[i],
            errorMessage: validation.errorMessage
          })
        }
      }

      if (droppedFiles.length > 0) {
        const message = droppedFiles.reduce((acc, invalidFile, index, allInvalidFiles) => {
          const line = invalidFile.file.name + ': ' + invalidFile.errorMessage
          return acc + (index > 0 ? '\n' + line : line)
        }, '')
        this.notifyError({ message })
      }

      if (tempFileArray.length > 0) {
        this.fileArray = [...this.fileArray, ...tempFileArray]
        const url = Vue.axios.agilisUrlTrackerAttachments(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId)

        Vue.axios.post(url, formData, {
          ...Vue.axios.agilisRequestConfig(url),
          onUploadProgress: (progress) => {
            this.updateProgress({ id: 'attachments-progress', progress })
          }
        }).then(response => {
          this.fetchFiles()
          this.$emit('componentUpdated')
        }).catch(error => {
          // remove the tmp file
          this.fileArray = this.fileArray.filter(f => f.id !== uploadID)

          // Show the error
          this.notifyError(error)
        }).finally(() => {
          // Reset the progress
          this.stopProgress({ id: 'attachments-progress' })
          this.keyReset = this.keyReset + 1
        })
      }
    },

    onTemplatePicked (event) {
      const files = event.target.files
      let formData = new FormData()
      let tempFileArray = []
      let droppedFiles = []

      const uploadID = Date.now()
      this.startProgress({ id: 'attachments-progress', value: uploadID })

      for (var i = 0; i < files.length; i++) {
        const validation = this.validateTemplateFile(files[i])
        if (validation.valid === true) {
          formData.append('files[' + i + ']', files[i])
          tempFileArray.push({ id: uploadID, contentSize: files[i].size, filename: files[i].name })
        } else {
          droppedFiles.push({
            file: files[i],
            errorMessage: validation.errorMessage
          })
        }
      }

      if (droppedFiles.length > 0) {
        const message = droppedFiles.reduce((acc, invalidFile, index, allInvalidFiles) => {
          const line = invalidFile.file.name + ': ' + invalidFile.errorMessage
          return acc + (index > 0 ? '\n' + line : line)
        }, '')
        this.notifyError({ message })
      }

      if (tempFileArray.length > 0) {
        this.fileArray = [...this.fileArray, ...tempFileArray]
        const url = Vue.axios.agilisUrlTrackerTemplateAttachments(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId)

        Vue.axios.post(url, formData, {
          ...Vue.axios.agilisRequestConfig(url),
          onUploadProgress: (progress) => {
            this.updateProgress({ id: 'attachments-progress', progress })
          }
        }).then(response => {
          this.fetchFiles()
          this.$emit('componentUpdated')
        }).catch(error => {
          // remove the tmp file
          this.fileArray = this.fileArray.filter(f => f.id !== uploadID)

          // Show the error
          this.notifyError(error)
        }).finally(() => {
          // Reset the progress
          this.stopProgress({ id: 'attachments-progress' })
          this.keyResetTemplate = this.keyResetTemplate + 1
        })
      }
    },

    chooseTemplate () {
      this.$refs.templateUpload.click()
    },

    onFileClick (attachment, download = true) {
      this.startLoader({ id: 'download-attachment', value: attachment.id })
      const url = Vue.axios.agilisUrlTrackerAttachmentsEdit(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId,
        attachment.id)
      const requestConfig = Vue.axios.agilisRequestConfig(url)
      requestConfig.responseType = 'blob'
      Vue.axios.get(url, requestConfig).then(response => {
        const blob = new Blob([response.data], { type: attachment.mimeType })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.target = '_blank'

        if (download) {
          link.download = attachment.filename
        } else {
          link.target = '_blank'
        }

        document.body.appendChild(link)
        link.click()
        URL.revokeObjectURL(link.href)
      }).catch(error => {
        this.notifyError(error)
      }).finally(() => {
        this.stopLoader({ id: 'download-attachment' })
      })
    },

    async onFileDeleteClick (attachmentId) {
      const userResponse = await this.notifyUserConfirm({
        title: this.$t('label.fileUploadDeleteTitle'),
        messages: [this.$t('label.fileUploadDeleteMessage')],
        confirm: this.$t('label.fileUploadDeleteYes'),
        cancel: this.$t('label.fileUploadDeleteNo')
      })

      if (userResponse) {
        this.startLoader({ id: 'delete-attachment', value: attachmentId })
        const url = Vue.axios.agilisUrlTrackerAttachmentsEdit(
          this.applicationId,
          this.trackerTypeId,
          this.trackerId,
          attachmentId)

        Vue.axios.delete(url, Vue.axios.agilisRequestConfig(url)).then(response => {
          this.fetchFiles()
        }).catch(error => {
          this.notifyError(error)
        }).finally(() => {
          this.stopLoader({ id: 'delete-attachment' })
        })
      }
    },

    // ////////////////////////////////////////////////////////////////
    // Drag & Drop
    // ////////////////////////////////////////////////////////////////
    onDragEnter (e) {
      if (this.userCanAddAttachment) {
        e.preventDefault()

        const container = this.$refs.outerContainer
        if (container.className.indexOf(FILE_UPLOAD_DRAG_OVER_CLASSES) === -1) {
          container.className += FILE_UPLOAD_DRAG_OVER_CLASSES
        }
      }
    },

    onDragLeave () {
      const container = this.$refs.outerContainer
      container.className = container.className.replace(FILE_UPLOAD_DRAG_OVER_CLASSES, '')
    },

    onDragOver (e) {
      this.onDragEnter(e)
    },

    onDrop (event) {
      if (this.userCanAddAttachment) {
        event.preventDefault()
        const files = event.dataTransfer.files
        if (files && files.length > 0) {
          this.onFilePicked({
            target: {
              files: event.dataTransfer.files
            }
          })
        }
        this.onDragLeave()
      }
    },
    // ////////////////////////////////////////////////////////////////

    // ////////////////////////////////////////////////////////////////
    // File Validation
    // ////////////////////////////////////////////////////////////////
    validateFile ({ name, type, size }) {
      const validations = [
        this.validateFileName(name),
        this.validateFileType(type),
        this.validateFileSize(size)
      ]

      return validations.find(v => v.valid === false) || validations[0]
    },

    validateTemplateFile ({ name, type, size }) {
      const validations = [
        this.validateFileName(name),
        this.validateTemplateType(type),
        this.validateFileSize(size)
      ]

      return validations.find(v => v.valid === false) || validations[0]
    },

    validateFileName (name) {
      const maxFileNameLength = this.validationRules.attachmentMaxNameLength

      const traversalRegExp = /\.\.[\\/]/ // substrings to lead to directory traversal attacks (../, ..\)
      // Not validating name for xss: vue will escape the HTML on render unless we use v-html

      const test = !traversalRegExp.test(name) && name.length <= maxFileNameLength
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationName') : null
      }
    },

    validateFileType (type) {
      const typeFamily = type.split('/')[0] + '/*' // image/png => image/*
      const test = this.accept.indexOf(type) > -1 ||
      this.accept.indexOf(typeFamily) > -1
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationFormat', { format: type }) : null
      }
    },

    validateTemplateType (type) {
      const test = this.acceptTemplate.indexOf(type) > -1
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationFormat', { format: type }) : null
      }
    },

    validateFileSize (sizeInBytes) {
      const maxSizeInMB = this.validationRules.attachmentMaxSize

      const sizeInMegaBytes = Math.round((sizeInBytes / 1048576))
      const test = sizeInMegaBytes < maxSizeInMB
      return {
        valid: test,
        errorMessage: !test ? this.$t('label.fileUploadValidationSize', { max: maxSizeInMB }) : null
      }
    },
    // ////////////////////////////////////////////////////////////////

    userCanPreviewAttachment (attachment) {
      const type = attachment.mimeType || ''
      const typeFamily = type.split('/')[0] + '/*' // image/png => image/*

      return type === 'application/pdf' || typeFamily === 'image/*'
    }
  }
}
